// src/lib/api/sourcesApi.ts
//
// Typed wrappers for the V1 /api/sources/* surface (see Nexilink backend
// controllers/sources.go). Visibility is scoped per the employer's unit
// + any sources promoted to its organisation — backend enforces; the
// frontend just passes the JWT (handled transparently by jasit-api-kit).

import { getApiClient } from './client';
import type { Source, CreateSourceFromURLRequest, ModuleRef } from '../models/source';
import CONFIG from '../config';

// Upload a file (PDF or image) as a source. Max 10 MB; backend enforces type whitelist.
const uploadSource = async (file: File): Promise<Source> => {
    const formData = new FormData();
    formData.append('file', file);
    return getApiClient().uploadMultipart<Source>('/api/sources', formData, 'post');
};

// Register a public URL as a source (no upload; content is fetched lazily at generation time).
const createURLSource = async (request: CreateSourceFromURLRequest): Promise<Source> => {
    return getApiClient().post<Source>('/api/sources/url', request);
};

// List sources visible to the caller (own unit + org-promoted).
const listSources = async (): Promise<Source[]> => {
    // Backend returns a bare array, not { sources: [...] } — matches reqres.NewSourceResponses.
    return getApiClient().get<Source[]>('/api/sources');
};

// Get one source by ID (visibility-checked server-side).
const getSource = async (id: string): Promise<Source> => {
    return getApiClient().get<Source>(`/api/sources/${id}`);
};

// Build a direct download URL for a file source. Same pattern as documentApi.
const getDownloadUrl = (id: string): string => {
    return `${CONFIG.API_BASE_URL}${CONFIG.API_AUTH_PORT}/api/sources/${id}/download`;
};

// Build the PUBLIC inline URL for an image source (no auth; renders in <img>).
// Backend serves only image-kind sources through this route.
const getPublicImageUrl = (id: string): string => {
    return `${CONFIG.API_BASE_URL}${CONFIG.API_AUTH_PORT}/api/public/source-images/${id}`;
};

// Delete a source. Backend refuses (400) if any module still references it; only the owner unit may delete.
const deleteSource = async (id: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/sources/${id}`);
};

// Promote a unit-owned source to org-wide visibility. Owner unit only.
const promoteSource = async (id: string): Promise<Source> => {
    return getApiClient().post<Source>(`/api/sources/${id}/promote`, {});
};

// Look up a source by SHA-256 hex content hash. Returns null on 404
// (the expected "no duplicate" path — not an error). Used by the V2
// chat duplicate-detection flow before staging an attachment.
const getSourceByHash = async (hash: string): Promise<Source | null> => {
    try {
        return await getApiClient().get<Source>(`/api/sources/by-hash/${encodeURIComponent(hash)}`);
    } catch (err: any) {
        // The kit's ApiClient throws on non-2xx; we want 404 to fall
        // through as "no match found" rather than an exception. Other
        // statuses (401/403/500) keep propagating.
        const status = err?.response?.status ?? err?.status;
        if (status === 404) return null;
        throw err;
    }
};

// Replace an existing source's bytes in place. Same source ID;
// module_sources references stay intact. Owner unit only.
const replaceSourceContent = async (id: string, file: File): Promise<Source> => {
    const formData = new FormData();
    formData.append('file', file);
    return getApiClient().uploadMultipart<Source>(`/api/sources/${id}/content`, formData, 'put');
};

// Download a source's bytes as a Blob (getDownloadUrl only builds a
// URL — this fetches the bytes so they can be re-encoded inline as
// an attachment). Used by the duplicate-detection "Use existing" path.
const downloadSourceBlob = async (id: string): Promise<Blob> => {
    const url = getDownloadUrl(id);
    const token = getApiClient().getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) {
        throw new Error(`Failed to download source ${id}: ${res.status} ${res.statusText}`);
    }
    return res.blob();
};

// List modules that reference this source via module_sources. Used
// by the source-detail view + the delete-confirmation dialog to show
// users exactly which modules they'd need to detach before delete.
// Visibility-checked server-side (caller must be able to see the
// source itself).
const listModulesUsingSource = async (id: string): Promise<ModuleRef[]> => {
    return getApiClient().get<ModuleRef[]>(`/api/sources/${id}/modules`);
};

export const sourcesApi = {
    uploadSource,
    createURLSource,
    listSources,
    getSource,
    getDownloadUrl,
    getPublicImageUrl,
    deleteSource,
    promoteSource,
    getSourceByHash,
    replaceSourceContent,
    downloadSourceBlob,
    listModulesUsingSource,
};
